import { useState, useMemo, useEffect } from 'react';
import { motion } from 'framer-motion';
import SubNav from '../../components/feature/SubNav';
import FilterSidebar from './components/FilterSidebar';
import JobCard from './components/JobCard';
import Pagination from './components/Pagination';
import Footer from '../home/components/Footer';
import { jobListData } from '../../mocks/jobList';
import KakaoFloatingButton from '../../components/feature/KakaoFloatingButton';
import { useSEO } from '../../utils/seo';

const ITEMS_PER_PAGE = 6;

export default function JobsPage() {
  const [scrolled, setScrolled] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('전체');
  const [selectedRegion, setSelectedRegion] = useState('전체');
  const [selectedWorkType, setSelectedWorkType] = useState('전체');
  const [sortBy, setSortBy] = useState<'latest' | 'salary' | 'popular'>('latest');
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useSEO({
    title: '채용정보 - 온니잡 | 나이트라이프 업계 구인구직',
    description: '유흥업, 바텐더, 모던바, 호스트바 등 나이트라이프 업계 최신 채용공고를 확인하세요. 실시간 업데이트되는 검증된 일자리 정보를 온니잡에서 만나보세요.',
    keywords: '유흥업 채용, 바텐더 구인, 모던바 채용, 호스트바 구인, 나이트라이프 일자리, 퀸알바, 여우알바',
    canonical: `${siteUrl}/jobs`,
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'BreadcrumbList',
          '@id': `${siteUrl}/jobs#breadcrumb`,
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: '홈',
              item: siteUrl
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: '채용정보',
              item: `${siteUrl}/jobs`
            }
          ]
        },
        {
          '@type': 'CollectionPage',
          '@id': `${siteUrl}/jobs#webpage`,
          name: '채용정보 - 온니잡',
          description: '나이트라이프 업계 채용공고 목록',
          url: `${siteUrl}/jobs`,
          inLanguage: 'ko-KR',
          isPartOf: { '@id': `${siteUrl}/#website` },
          breadcrumb: { '@id': `${siteUrl}/jobs#breadcrumb` }
        },
        {
          '@type': 'ItemList',
          '@id': `${siteUrl}/jobs#joblist`,
          name: '나이트라이프 업계 채용공고',
          description: '온니잡에서 제공하는 최신 나이트라이프 업계 채용공고 목록',
          url: `${siteUrl}/jobs`,
          numberOfItems: jobListData.length,
          itemListElement: jobListData.slice(0, 10).map((job, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            item: {
              '@type': 'JobPosting',
              '@id': `${siteUrl}/job-detail/${job.id}`,
              title: job.position,
              description: `${job.company}에서 ${job.position} 포지션을 모집합니다. 지역: ${job.region} ${job.location}, 급여: ${job.salary}`,
              datePosted: job.postedDate,
              validThrough: '2025-12-31',
              employmentType: job.workType === '정규직' ? 'FULL_TIME' : job.workType === '아르바이트' ? 'PART_TIME' : 'CONTRACTOR',
              hiringOrganization: {
                '@type': 'Organization',
                name: job.company,
                sameAs: siteUrl
              },
              jobLocation: {
                '@type': 'Place',
                address: {
                  '@type': 'PostalAddress',
                  addressLocality: job.location,
                  addressRegion: job.region,
                  addressCountry: 'KR'
                }
              },
              baseSalary: {
                '@type': 'MonetaryAmount',
                currency: 'KRW',
                value: {
                  '@type': 'QuantitativeValue',
                  value: job.salaryNum * 10000,
                  unitText: 'MONTH'
                }
              },
              occupationalCategory: job.category,
              url: `${siteUrl}/job-detail/${job.id}`
            }
          }))
        }
      ]
    }
  });

  const filteredJobs = useMemo(() => {
    let result = [...jobListData];

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (j) =>
          j.position.toLowerCase().includes(q) ||
          j.company.toLowerCase().includes(q)
      );
    }
    if (selectedCategory !== '전체') {
      result = result.filter((j) => j.category === selectedCategory);
    }
    if (selectedRegion !== '전체') {
      result = result.filter((j) => j.region === selectedRegion);
    }
    if (selectedWorkType !== '전체') {
      result = result.filter((j) => j.workType === selectedWorkType);
    }

    if (sortBy === 'latest') {
      result.sort((a, b) => new Date(b.postedDate).getTime() - new Date(a.postedDate).getTime());
    } else if (sortBy === 'salary') {
      result.sort((a, b) => b.salaryNum - a.salaryNum);
    } else {
      result.sort((a, b) => b.views - a.views);
    }

    return result;
  }, [searchQuery, selectedCategory, selectedRegion, selectedWorkType, sortBy]);

  const totalPages = Math.max(1, Math.ceil(filteredJobs.length / ITEMS_PER_PAGE));
  const paginatedJobs = filteredJobs.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handleReset = () => {
    setSearchQuery('');
    setSelectedCategory('전체');
    setSelectedRegion('전체');
    setSelectedWorkType('전체');
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-dark-deep">
      <SubNav scrolled={scrolled} />

      {/* Hero */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-neon-purple/8 via-transparent to-transparent"></div>
        <div className="container mx-auto px-10 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <span className="inline-block text-xs font-bold text-neon-pink border border-neon-pink/30 rounded-full px-4 py-1.5 mb-5">
              실시간 업데이트
            </span>
            <h2 className="text-5xl font-display font-black text-white mb-4">
              <a href="/jobs">채용정보</a>
            </h2>
            <p className="text-base text-gray-400 max-w-lg mx-auto">
              나이트라이프 업계 최고의 포지션을 찾아보세요
            </p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <main className="container mx-auto px-10 pb-20">
        <div className="flex gap-8">
          {/* Sidebar */}
          <FilterSidebar
            selectedCategory={selectedCategory}
            selectedRegion={selectedRegion}
            selectedWorkType={selectedWorkType}
            searchQuery={searchQuery}
            onCategoryChange={(v) => { setSelectedCategory(v); setCurrentPage(1); }}
            onRegionChange={(v) => { setSelectedRegion(v); setCurrentPage(1); }}
            onWorkTypeChange={(v) => { setSelectedWorkType(v); setCurrentPage(1); }}
            onSearchChange={(v) => { setSearchQuery(v); setCurrentPage(1); }}
            onReset={handleReset}
          />

          {/* Job List */}
          <div className="flex-1 min-w-0">
            {/* Sort Bar */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="flex items-center justify-between mb-8"
            >
              <p className="text-sm text-gray-400">
                총 <strong className="text-white">{filteredJobs.length}</strong>개의 공고
              </p>
              <div className="flex items-center gap-1 bg-white/5 rounded-xl p-1">
                {[
                  { key: 'latest' as const, label: '최신순' },
                  { key: 'salary' as const, label: '급여순' },
                  { key: 'popular' as const, label: '인기순' },
                ].map((s) => (
                  <button
                    key={s.key}
                    onClick={() => { setSortBy(s.key); setCurrentPage(1); }}
                    className={`px-4 py-2 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                      sortBy === s.key
                        ? 'bg-neon-purple text-white'
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </motion.div>

            {/* Grid */}
            {paginatedJobs.length > 0 ? (
              <div className="grid grid-cols-3 gap-5">
                {paginatedJobs.map((job, index) => (
                  <JobCard key={job.id} job={job} index={index} />
                ))}
              </div>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex flex-col items-center justify-center py-32"
              >
                <i className="ri-search-line text-5xl text-gray-600 mb-4 w-14 h-14 flex items-center justify-center"></i>
                <p className="text-lg text-gray-500 mb-2">검색 결과가 없습니다</p>
                <p className="text-sm text-gray-600">다른 조건으로 검색해보세요</p>
              </motion.div>
            )}

            {/* Pagination */}
            {filteredJobs.length > ITEMS_PER_PAGE && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            )}
          </div>
        </div>
      </main>

      <Footer />
      <KakaoFloatingButton />
    </div>
  );
}
